// Pure helpers for the Stores section (ops/stores and ops/stores/{id}).

export const RADIUS_MIN_KM = 2;
export const RADIUS_MAX_KM = 10;
const PICKER_LIMIT = 20;

const STANDINGS = {
  ACTIVE: { tone: "ok", label: "Active" },
  WARNED: { tone: "warn", label: "Warned" },
  PAUSED: { tone: "warn", label: "Paused" },
  REMOVED: { tone: "bad", label: "Removed" },
};

// The status badge for a store row or detail.
export const standingBadge = (store) => {
  if (!store?.standing) return { tone: "", label: "Not in D2C" };
  return STANDINGS[store.standing] || { tone: "", label: String(store.standing) };
};

// How the store joined: by an admin, by auto-enrol, or from the dealer app.
export const enrolledLabel = (store) => {
  if (!store?.standing) return "-";
  if (store.enrolled_via === "ADMIN") return "Admin";
  if (store.enrolled_via === "AUTO") return "Auto";
  return "App";
};

const plural = (count, word) => `${count} ${word}${Number(count) === 1 ? "" : "s"}`;

// An optional radius typed by the user: null when fine (or blank), else why not.
// The store's own range can be narrower; the server has the last word.
export const radiusError = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return null;
  const number = Number(text);
  if (!Number.isInteger(number) || number < RADIUS_MIN_KM || number > RADIUS_MAX_KM) {
    return `Enter a whole number of km from ${RADIUS_MIN_KM} to ${RADIUS_MAX_KM}.`;
  }
  return null;
};

// The PUT body for a store switch.
export const storeSwitchBody = (enabled, radius) => {
  const text = String(radius ?? "").trim();
  return enabled && text ? { enabled: true, serving_radius_km: Number(text) } : { enabled };
};

// What a successful PUT did, in plain words.
export const switchOutcome = (enabled, response) => {
  const changed = response?.changed || [];
  if (enabled) {
    const joined = changed.includes("created") || changed.includes("lifted");
    const parts = [];
    if (joined) parts.push(`Switched on. ${plural(response?.users_granted || 0, "dealer user")} given D2C access.`);
    if (changed.includes("serving_radius_km")) parts.push(`Serving radius is now ${response?.serving_radius_km} km.`);
    return parts.length ? parts.join(" ") : "Nothing changed: the store was already in D2C.";
  }
  if (!changed.length) return "Nothing changed: the store was already switched off.";
  return `Switched off. ${plural(response?.withdrawn_offers || 0, "open offer")} withdrawn; ${plural(
    response?.orders_closing || 0,
    "order",
  )} going back to central within a minute.`;
};

// The franchise list filtered for the "find a store" picker.
export const pickStores = (franchises, search) => {
  const term = String(search || "").trim().toLowerCase();
  if (!term) return [];
  return (franchises || [])
    .filter((item) =>
      [item?.customer_name, item?.franchise_code].some((value) => String(value || "").toLowerCase().includes(term)),
    )
    .slice(0, PICKER_LIMIT);
};

// Whether the stored franchise_list reply (Redux AllDealerFranchiseTypeListAll)
// is a successful list. ws_response.js stores success and failure replies in the
// same place, and the reducer keeps only {list, pin, franchise_type}: a failure
// (the server's BAD_CODE with an empty payload, which it also sends when no
// franchise matches) lands as empty arrays. So success means a non-empty list
// of rows that each carry an id.
export const franchiseReplyOk = (stored) =>
  Array.isArray(stored?.list) && stored.list.length > 0 && stored.list.every((item) => Boolean(item?.id));
